/**
 * Comprehensive Chat & Expense Parsing Tests
 * Tests real-world chat scenarios with edge function integration
 */

export interface ChatTestCase {
  id: string;
  category: string;
  scenario: string;
  userMessages: string[];
  expectedBehavior: {
    shouldParseExpense: boolean;
    shouldAskClarification?: boolean;
    clarificationField?: 'amount' | 'merchant' | 'category';
    finalExpense?: {
      amount: number;
      currency: string;
      merchant: string;
      category: string;
    };
  };
  notes: string;
}

export const chatTests: ChatTestCase[] = [
  // ===== Category 1: Single-Turn Complete Inputs =====
  {
    id: "chat-01",
    category: "Single-Turn Complete",
    scenario: "User provides complete expense in one message",
    userMessages: ["$45 Starbucks"],
    expectedBehavior: {
      shouldParseExpense: true,
      finalExpense: {
        amount: 45.00,
        currency: "USD",
        merchant: "Starbucks",
        category: "Food"
      }
    },
    notes: "Should parse immediately without clarification"
  },
  {
    id: "chat-02",
    category: "Single-Turn Complete",
    scenario: "User provides expense with date",
    userMessages: ["coffee yesterday 5.50€"],
    expectedBehavior: {
      shouldParseExpense: true,
      finalExpense: {
        amount: 5.50,
        currency: "EUR",
        merchant: "Coffee Shop",
        category: "Food"
      }
    },
    notes: "Should handle relative dates correctly"
  },
  {
    id: "chat-03",
    category: "Single-Turn Complete",
    scenario: "User provides Ukrainian expense",
    userMessages: ["продукти 350₴ АТБ"],
    expectedBehavior: {
      shouldParseExpense: true,
      finalExpense: {
        amount: 350.00,
        currency: "UAH",
        merchant: "АТБ",
        category: "Food"
      }
    },
    notes: "Should handle Ukrainian keywords and currency"
  },

  // ===== Category 2: Multi-Turn Clarification Flows =====
  {
    id: "chat-04",
    category: "Multi-Turn Clarification",
    scenario: "User forgets amount, AI asks for it",
    userMessages: ["Starbucks yesterday", "5.50"],
    expectedBehavior: {
      shouldParseExpense: true,
      shouldAskClarification: true,
      clarificationField: 'amount',
      finalExpense: {
        amount: 5.50,
        currency: "EUR",
        merchant: "Starbucks",
        category: "Food"
      }
    },
    notes: "AI should ask 'How much?' and remember merchant"
  },
  {
    id: "chat-05",
    category: "Multi-Turn Clarification",
    scenario: "User forgets merchant, AI asks for it",
    userMessages: ["€25 yesterday", "McDonald's"],
    expectedBehavior: {
      shouldParseExpense: true,
      shouldAskClarification: true,
      clarificationField: 'merchant',
      finalExpense: {
        amount: 25.00,
        currency: "EUR",
        merchant: "McDonald's",
        category: "Food"
      }
    },
    notes: "AI should ask 'What did you buy?' and remember amount"
  },
  {
    id: "chat-06",
    category: "Multi-Turn Clarification",
    scenario: "User provides partial info, then completes in multiple turns",
    userMessages: ["lunch", "15 euros", "KFC"],
    expectedBehavior: {
      shouldParseExpense: true,
      shouldAskClarification: true,
      finalExpense: {
        amount: 15.00,
        currency: "EUR",
        merchant: "KFC",
        category: "Food"
      }
    },
    notes: "Should accumulate context across multiple turns"
  },

  // ===== Category 3: Natural Language & Complex Inputs =====
  {
    id: "chat-07",
    category: "Natural Language",
    scenario: "User uses casual speech",
    userMessages: ["spent like twenty bucks on chipotle today"],
    expectedBehavior: {
      shouldParseExpense: true,
      finalExpense: {
        amount: 20.00,
        currency: "USD",
        merchant: "Chipotle",
        category: "Food"
      }
    },
    notes: "Should parse natural speech patterns"
  },
  {
    id: "chat-08",
    category: "Natural Language",
    scenario: "User provides long descriptive input",
    userMessages: ["Had a great dinner with friends at this amazing Italian restaurant downtown, spent about €75 on pasta and wine last Friday"],
    expectedBehavior: {
      shouldParseExpense: true,
      finalExpense: {
        amount: 75.00,
        currency: "EUR",
        merchant: "Restaurant",
        category: "Food"
      }
    },
    notes: "Should extract key info from long text and create note"
  },

  // ===== Category 4: Error Handling & Edge Cases =====
  {
    id: "chat-09",
    category: "Error Handling",
    scenario: "User sends completely unrelated message",
    userMessages: ["hello how are you"],
    expectedBehavior: {
      shouldParseExpense: false,
      shouldAskClarification: true
    },
    notes: "Should ask for expense details or guide user"
  },
  {
    id: "chat-10",
    category: "Error Handling",
    scenario: "User sends very ambiguous amount",
    userMessages: ["walmart about 20-25"],
    expectedBehavior: {
      shouldParseExpense: true,
      finalExpense: {
        amount: 22.50, // Should pick middle or first value
        currency: "EUR",
        merchant: "Walmart",
        category: "Food"
      }
    },
    notes: "Should handle range amounts gracefully"
  },

  // ===== Category 5: Multi-Item Inputs =====
  {
    id: "chat-11",
    category: "Multi-Item",
    scenario: "User mentions multiple items",
    userMessages: ["Coffee $5 and lunch $15 at Whole Foods"],
    expectedBehavior: {
      shouldParseExpense: true,
      finalExpense: {
        amount: 20.00, // Should sum or create separate expenses
        currency: "USD",
        merchant: "Whole Foods",
        category: "Food"
      }
    },
    notes: "Should handle multi-item parsing (sum or split)"
  },

  // ===== Category 6: Confirmation & Correction Flow =====
  {
    id: "chat-12",
    category: "Confirmation Flow",
    scenario: "User confirms expense then wants to log another",
    userMessages: ["$45 Starbucks", "yes", "$20 Uber"],
    expectedBehavior: {
      shouldParseExpense: true,
      finalExpense: {
        amount: 20.00,
        currency: "USD",
        merchant: "Uber",
        category: "Transport"
      }
    },
    notes: "Should handle confirmation and new expense in sequence"
  },

  // ===== Category 7: Different Languages =====
  {
    id: "chat-13",
    category: "Multi-Language",
    scenario: "User switches languages mid-conversation",
    userMessages: ["coffee €5", "і ще taxi 50₴"],
    expectedBehavior: {
      shouldParseExpense: true,
      finalExpense: {
        amount: 50.00,
        currency: "UAH",
        merchant: "Taxi",
        category: "Transport"
      }
    },
    notes: "Should handle language switching gracefully"
  },

  // ===== Category 8: Typos & Fuzzy Matching =====
  {
    id: "chat-14",
    category: "Typos & Fuzzy",
    scenario: "User misspells brand name",
    userMessages: ["Wallmart 35$"],
    expectedBehavior: {
      shouldParseExpense: true,
      finalExpense: {
        amount: 35.00,
        currency: "USD",
        merchant: "Walmart",
        category: "Food"
      }
    },
    notes: "Should tolerate common typos in brand names"
  },

  // ===== Category 9: Voice Input Simulation =====
  {
    id: "chat-15",
    category: "Voice Input",
    scenario: "Voice input with filler words",
    userMessages: ["um so like I spent uh fifteen euros at um Starbucks"],
    expectedBehavior: {
      shouldParseExpense: true,
      finalExpense: {
        amount: 15.00,
        currency: "EUR",
        merchant: "Starbucks",
        category: "Food"
      }
    },
    notes: "Should filter out filler words from voice input"
  }
];
